import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import { FaHome, FaTrophy, FaChartLine, FaGraduationCap, FaDollarSign, FaMedal, FaStar, FaWallet, FaArrowLeft } from 'react-icons/fa';
import AuthService from '../services/AuthService';
import LeaderboardService from '../services/LeaderboardService';
import UserStatsModal from './UserStatsModal';
import toast from 'react-hot-toast';
import soundEffects from '../utils/soundEffects';
import { createRipple, createPulse, createSparkle, createGlow } from '../utils/vfx';
import './Leaderboard.css';

const Leaderboard = () => {
  const navigate = useNavigate();
  const { state } = useUser();
  const [leaderboard, setLeaderboard] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [userGlobalRank, setUserGlobalRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leaderboardType, setLeaderboardType] = useState('portfolio'); // 'portfolio', 'total', 'investing', 'quizzes'
  const [selectedLeague, setSelectedLeague] = useState(null); // null = all leagues, '10k', '100k', '1M'
  const [myRank, setMyRank] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState('');
  const [selectedUserProfilePicture, setSelectedUserProfilePicture] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (!AuthService.isAuthenticated()) {
          navigate('/login', { replace: true });
          return;
        }

        if (!state.user) {
          navigate('/login', { replace: true });
          return;
        }

        await loadLeaderboard();
        await loadMyRank();
        soundEffects.playPageTransition();
      } catch (error) {
        console.error('Error loading leaderboard:', error);
        toast.error('Failed to load leaderboard');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate, state.user, leaderboardType, selectedLeague]);

  const loadLeaderboard = async () => {
    try {
      const data = await LeaderboardService.getLeaderboard(leaderboardType, 100, selectedLeague);
      setLeaderboard(data.leaderboard || []);
      setCurrentUser(data.currentUser);
      setUserGlobalRank(data.userGlobalRank);
      setTotalUsers(data.totalUsers || 0);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
      toast.error('Failed to load leaderboard');
    }
  };

  const loadMyRank = async () => {
    try {
      const rankData = await LeaderboardService.getMyRank(leaderboardType, selectedLeague);
      setMyRank(rankData);
    } catch (error) {
      console.error('Error loading my rank:', error);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <FaMedal className="rank-icon gold" />;
    if (rank === 2) return <FaMedal className="rank-icon silver" />;
    if (rank === 3) return <FaMedal className="rank-icon bronze" />;
    return <span className="rank-number">#{rank}</span>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'portfolio':
        return 'Portfolio Value';
      case 'investing':
        return 'Investing Earnings';
      case 'quizzes':
        return 'Quiz Earnings';
      case 'total':
        return 'Total Earnings';
      default:
        return 'Portfolio Value';
    }
  };

  const getTypeValue = (entry, type) => {
    switch (type) {
      case 'portfolio':
        return entry.totalValue;
      case 'investing':
        return entry.investingEarnings;
      case 'quizzes':
        return entry.quizEarnings;
      case 'total':
        return entry.totalEarnings;
      default:
        return entry.totalValue;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getLeagueLabel = (league) => {
    switch (league) {
      case '10k':
        return '$10K League';
      case '100k':
        return '$100K League';
      case '1M':
        return '$1M League';
      default:
        return 'All Leagues';
    }
  };

  const handleProfileClick = (entry, e) => {
    soundEffects.playClick();
    if (e) createRipple(e);
    setSelectedUserId(entry.userId);
    setSelectedUserName(entry.name);
    setSelectedUserProfilePicture(entry.profilePictureUrl);
    setShowStatsModal(true);
  };

  const handleCloseModal = () => {
    soundEffects.playClick();
    setShowStatsModal(false);
    setSelectedUserId(null);
    setSelectedUserName('');
    setSelectedUserProfilePicture(null);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const getDefaultAvatarColor = (name) => {
    if (!name) return '#2563eb';
    const colors = [
      '#2563eb', '#10b981', '#f59e0b', '#ef4444', 
      '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  if (loading) {
    return (
      <div className="leaderboard-container">
        <div className="loading-spinner">Loading leaderboard...</div>
      </div>
    );
  }

  return (
    <div className="leaderboard-container">
      <div className="leaderboard-header">
        <div className="header-left">
          <button className="back-button" onClick={(e) => {
            soundEffects.playClick();
            createRipple(e);
            navigate('/dashboard');
          }} onMouseEnter={() => soundEffects.playHover()}>
            <FaArrowLeft />
          </button>
          <h1 className="leaderboard-title">
            <FaTrophy /> Leaderboard
            {selectedLeague && (
              <span className="league-badge">{getLeagueLabel(selectedLeague)}</span>
            )}
          </h1>
        </div>
      </div>

      <div className="leaderboard-controls">
        <div className="league-selector">
          <button
            className={`league-button ${selectedLeague === null ? 'active' : ''}`}
            onClick={(e) => {
              soundEffects.playClick();
              createRipple(e);
              setSelectedLeague(null);
            }}
            onMouseEnter={() => soundEffects.playHover()}
          >
            All Leagues
          </button>
          <button
            className={`league-button ${selectedLeague === '10k' ? 'active' : ''}`}
            onClick={(e) => {
              soundEffects.playClick();
              createRipple(e);
              setSelectedLeague('10k');
            }}
            onMouseEnter={() => soundEffects.playHover()}
          >
            $10K League
          </button>
          <button
            className={`league-button ${selectedLeague === '100k' ? 'active' : ''}`}
            onClick={(e) => {
              soundEffects.playClick();
              createRipple(e);
              setSelectedLeague('100k');
            }}
            onMouseEnter={() => soundEffects.playHover()}
          >
            $100K League
          </button>
          <button
            className={`league-button ${selectedLeague === '1M' ? 'active' : ''}`}
            onClick={(e) => {
              soundEffects.playClick();
              createRipple(e);
              setSelectedLeague('1M');
            }}
            onMouseEnter={() => soundEffects.playHover()}
          >
            $1M League
          </button>
        </div>

        <div className="type-selector">
          <button
            className={`type-button ${leaderboardType === 'portfolio' ? 'active' : ''}`}
            onClick={(e) => {
              soundEffects.playClick();
              createRipple(e);
              setLeaderboardType('portfolio');
            }}
            onMouseEnter={() => soundEffects.playHover()}
          >
            <FaWallet /> Portfolio Value
          </button>
          <button
            className={`type-button ${leaderboardType === 'total' ? 'active' : ''}`}
            onClick={(e) => {
              soundEffects.playClick();
              createRipple(e);
              setLeaderboardType('total');
            }}
            onMouseEnter={() => soundEffects.playHover()}
          >
            <FaDollarSign /> Total Earnings
          </button>
          <button
            className={`type-button ${leaderboardType === 'investing' ? 'active' : ''}`}
            onClick={(e) => {
              soundEffects.playClick();
              createRipple(e);
              setLeaderboardType('investing');
            }}
            onMouseEnter={() => soundEffects.playHover()}
          >
            <FaChartLine /> Investing
          </button>
          <button
            className={`type-button ${leaderboardType === 'quizzes' ? 'active' : ''}`}
            onClick={(e) => {
              soundEffects.playClick();
              createRipple(e);
              setLeaderboardType('quizzes');
            }}
            onMouseEnter={() => soundEffects.playHover()}
          >
            <FaGraduationCap /> Quizzes
          </button>
        </div>

        {myRank && (
          <div className="my-rank-card">
            <div className="rank-label">Your Rank</div>
            <div className="rank-value">#{myRank.rank}</div>
            <div className="rank-stats">
              <div className="stat-item">
                <span className="stat-label">Total Earnings:</span>
                <span className="stat-value">{formatCurrency(myRank.stats.totalEarnings)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Investing:</span>
                <span className="stat-value">{formatCurrency(myRank.stats.investingEarnings)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Quizzes:</span>
                <span className="stat-value">{formatCurrency(myRank.stats.quizEarnings)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="leaderboard-content">
        <div className="leaderboard-list">
          {leaderboard.length === 0 ? (
            <div className="empty-state">
              <FaTrophy className="empty-icon" />
              <p>No rankings yet. Be the first!</p>
            </div>
          ) : (
            leaderboard.map((entry, index) => {
              const isCurrentUser = currentUser && entry.userId === currentUser.userId;
              const value = getTypeValue(entry, leaderboardType);
              
              return (
                <motion.div
                  key={entry.userId}
                  className={`leaderboard-entry ${isCurrentUser ? 'current-user' : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="entry-rank">
                    {getRankIcon(entry.rank)}
                  </div>
                  <div 
                    className="entry-avatar-container"
                    onClick={(e) => handleProfileClick(entry, e)}
                    onMouseEnter={(e) => {
                      soundEffects.playHover();
                      createPulse(e.currentTarget, 1.05, 200);
                    }}
                    title="Click to view profile"
                  >
                    {entry.profilePictureUrl ? (
                      <img 
                        src={entry.profilePictureUrl} 
                        alt={entry.name}
                        className="entry-avatar"
                      />
                    ) : (
                      <div 
                        className="entry-avatar-default"
                        style={{ backgroundColor: getDefaultAvatarColor(entry.name) }}
                      >
                        <span className="entry-avatar-initials">{getInitials(entry.name)}</span>
                      </div>
                    )}
                  </div>
                  <div className="entry-info">
                    <div className="entry-name">
                      {entry.name}
                      {isCurrentUser && <span className="you-badge">You</span>}
                    </div>
                    <div className="entry-details">
                      <div className="detail-item">
                        <span className="detail-label">Total:</span>
                        <span className="detail-value">{formatCurrency(entry.totalEarnings)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Investing:</span>
                        <span className={`detail-value ${entry.investingEarnings >= 0 ? 'positive' : 'negative'}`}>
                          {formatCurrency(entry.investingEarnings)}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Quizzes:</span>
                        <span className="detail-value positive">{formatCurrency(entry.quizEarnings)}</span>
                      </div>
                      {entry.portfolioCreatedAt && (
                        <div className="detail-item">
                          <span className="detail-label">Launched:</span>
                          <span className="detail-value">{formatDate(entry.portfolioCreatedAt)}</span>
                        </div>
                      )}
                      {entry.league && (
                        <div className="detail-item">
                          <span className="detail-label">League:</span>
                          <span className="detail-value">{getLeagueLabel(entry.league)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="entry-primary-value">
                    <div className="primary-label">{getTypeLabel(leaderboardType)}</div>
                    <div className={`primary-amount ${value >= 0 ? 'positive' : 'negative'}`}>
                      {formatCurrency(value)}
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        {userGlobalRank && currentUser && !leaderboard.some(e => e.userId === currentUser.userId) && (
          <div className="your-entry-separator">
            <div className="separator-line"></div>
            <div className="separator-text">Your Position</div>
            <div className="separator-line"></div>
          </div>
        )}

        {currentUser && !leaderboard.some(e => e.userId === currentUser.userId) && userGlobalRank && (
          <motion.div
            className="leaderboard-entry current-user global"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="entry-rank">
              <span className="rank-number">#{userGlobalRank}</span>
            </div>
            <div 
              className="entry-avatar-container"
              onClick={(e) => handleProfileClick(currentUser, e)}
              onMouseEnter={(e) => {
                soundEffects.playHover();
                createPulse(e.currentTarget, 1.05, 200);
              }}
              title="Click to view profile"
            >
              {currentUser.profilePictureUrl ? (
                <img 
                  src={currentUser.profilePictureUrl} 
                  alt={currentUser.name}
                  className="entry-avatar"
                />
              ) : (
                <div 
                  className="entry-avatar-default"
                  style={{ backgroundColor: getDefaultAvatarColor(currentUser.name) }}
                >
                  <span className="entry-avatar-initials">{getInitials(currentUser.name)}</span>
                </div>
              )}
            </div>
            <div className="entry-info">
              <div className="entry-name">
                {currentUser.name}
                <span className="you-badge">You</span>
              </div>
              <div className="entry-details">
                <div className="detail-item">
                  <span className="detail-label">Total:</span>
                  <span className="detail-value">{formatCurrency(currentUser.totalEarnings)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Investing:</span>
                  <span className={`detail-value ${currentUser.investingEarnings >= 0 ? 'positive' : 'negative'}`}>
                    {formatCurrency(currentUser.investingEarnings)}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Quizzes:</span>
                  <span className="detail-value positive">{formatCurrency(currentUser.quizEarnings)}</span>
                </div>
              </div>
            </div>
            <div className="entry-primary-value">
              <div className="primary-label">{getTypeLabel(leaderboardType)}</div>
              <div className={`primary-amount ${getTypeValue(currentUser, leaderboardType) >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(getTypeValue(currentUser, leaderboardType))}
              </div>
            </div>
          </motion.div>
        )}
      </div>

      <div className="leaderboard-footer">
        <p>Total Players: {totalUsers}</p>
      </div>

      {/* User Stats Modal */}
      <UserStatsModal
        isOpen={showStatsModal}
        onClose={handleCloseModal}
        userId={selectedUserId}
        userName={selectedUserName}
        userProfilePictureUrl={selectedUserProfilePicture}
      />
    </div>
  );
};

export default Leaderboard;



